import HttpFactory from "~/repository/factory";
import { ENDPOINTS } from "~/repository/endpoints";
import {
  createPublicRequestConfig,
  type AuthRequestConfig,
} from "~/repository/authSession";
import type {
  VideoCallInvitation,
  VideoCallInvitationPreview,
  VideoCallConnectionResponse,
  VideoCallCreateResponse,
  VideoCallCurrentResponse,
  VideoCallInvitationResponse,
  VideoCallJoinRequest,
  VideoCallJoinRequestsResponse,
  VideoCallJoinRequestResponse,
  VideoCallJoinResponse,
  VideoCallStudentGoogleSignupInput,
  VideoCallStudentPasswordSignupInput,
  VideoCallStudentSignupEligibility,
  VideoCallStudentSignupResponse,
  VideoCallRoomsResponse,
  VideoCallRoomsSaveInput,
} from "~/types/videoCalls";
import { normalizeEmailInput } from "~/utils/email";

const withId = (template: string, callId: string) =>
  template.replace("{callId}", encodeURIComponent(callId));

const withJoinRequestId = (template: string, joinRequestId: string) =>
  template.replace("{joinRequestId}", encodeURIComponent(joinRequestId));

const withParticipantId = (template: string, participantId: string) =>
  template.replace("{participantId}", encodeURIComponent(participantId));

const guestRequestConfig = (guestSecret: string) =>
  createPublicRequestConfig({
    headers: {
      Authorization: `Guest ${guestSecret}`,
    },
  });

export default class VideoCallsModule extends HttpFactory {
  async getRooms(callId: string): Promise<VideoCallRoomsResponse> {
    return await this.get(withId(ENDPOINTS.VIDEO_CALLS.ROOMS, callId));
  }

  async saveRooms(
    callId: string,
    revision: number,
    input: VideoCallRoomsSaveInput,
  ): Promise<VideoCallRoomsResponse> {
    return await this.put(withId(ENDPOINTS.VIDEO_CALLS.ROOMS, callId), {
      ...input,
      revision,
    });
  }

  async openRooms(
    callId: string,
    revision: number,
  ): Promise<VideoCallRoomsResponse> {
    return await this.post(withId(ENDPOINTS.VIDEO_CALLS.OPEN_ROOMS, callId), {
      revision,
    });
  }

  async closeRooms(
    callId: string,
    revision: number,
  ): Promise<VideoCallRoomsResponse> {
    return await this.post(withId(ENDPOINTS.VIDEO_CALLS.CLOSE_ROOMS, callId), {
      revision,
    });
  }

  async moveToRoom(
    callId: string,
    revision: number,
    participantId: string,
    roomId: string,
  ): Promise<VideoCallRoomsResponse> {
    return await this.post(
      withId(ENDPOINTS.VIDEO_CALLS.MOVE_ROOM_PARTICIPANT, callId),
      {
        revision,
        participant_id: participantId,
        room_id: roomId,
      },
    );
  }

  async sendRoomAnnouncement(
    callId: string,
    body: string,
    messageId: string,
  ): Promise<VideoCallRoomsResponse> {
    return await this.post(
      withId(ENDPOINTS.VIDEO_CALLS.ROOM_ANNOUNCEMENTS, callId),
      {
        body,
        message_id: messageId,
      },
    );
  }

  async getGuestRooms(
    joinRequestId: string,
    secret: string,
  ): Promise<VideoCallRoomsResponse> {
    return await this.get(
      withJoinRequestId(ENDPOINTS.VIDEO_CALLS.GUEST_ROOMS, joinRequestId),
      {},
      guestRequestConfig(secret),
    );
  }

  async returnToMainRoom(
    joinRequestId: string,
    secret: string,
  ): Promise<VideoCallRoomsResponse> {
    return await this.post(
      withJoinRequestId(
        ENDPOINTS.VIDEO_CALLS.GUEST_RETURN_TO_MAIN,
        joinRequestId,
      ),
      {},
      guestRequestConfig(secret),
    );
  }

  async requestRoomHelp(
    joinRequestId: string,
    secret: string,
  ): Promise<VideoCallRoomsResponse> {
    return await this.post(
      withJoinRequestId(ENDPOINTS.VIDEO_CALLS.GUEST_ROOM_HELP, joinRequestId),
      {},
      guestRequestConfig(secret),
    );
  }

  async cancelRoomHelp(
    joinRequestId: string,
    secret: string,
  ): Promise<VideoCallRoomsResponse> {
    return await this.delete(
      withJoinRequestId(ENDPOINTS.VIDEO_CALLS.GUEST_ROOM_HELP, joinRequestId),
      guestRequestConfig(secret),
    );
  }

  async createCall(): Promise<VideoCallCreateResponse> {
    return await this.post<VideoCallCreateResponse>(
      ENDPOINTS.VIDEO_CALLS.CALLS,
      {},
    );
  }

  async getCurrentCall(): Promise<VideoCallCurrentResponse> {
    return await this.get<VideoCallCurrentResponse>(
      ENDPOINTS.VIDEO_CALLS.CURRENT,
    );
  }

  async updateHostConnection(
    callId: string,
    state: "connected" | "disconnected",
  ): Promise<VideoCallConnectionResponse> {
    return await this.post<VideoCallConnectionResponse>(
      withId(ENDPOINTS.VIDEO_CALLS.CONNECTION, callId),
      { state },
    );
  }

  async rotateInvitation(callId: string): Promise<VideoCallInvitation> {
    const response = await this.post<VideoCallInvitationResponse>(
      withId(ENDPOINTS.VIDEO_CALLS.ROTATE_INVITATION, callId),
      {},
    );
    return response.invitation;
  }

  async getJoinRequests(callId: string): Promise<VideoCallJoinRequest[]> {
    const response = await this.get<VideoCallJoinRequestsResponse>(
      withId(ENDPOINTS.VIDEO_CALLS.JOIN_REQUESTS, callId),
    );
    return response.join_requests;
  }

  async approveJoinRequest(
    callId: string,
    joinRequestId: string,
  ): Promise<VideoCallJoinRequestResponse> {
    return await this.post<VideoCallJoinRequestResponse>(
      withJoinRequestId(
        withId(ENDPOINTS.VIDEO_CALLS.APPROVE_JOIN_REQUEST, callId),
        joinRequestId,
      ),
      {},
    );
  }

  async denyJoinRequest(
    callId: string,
    joinRequestId: string,
  ): Promise<VideoCallJoinRequestResponse> {
    return await this.post<VideoCallJoinRequestResponse>(
      withJoinRequestId(
        withId(ENDPOINTS.VIDEO_CALLS.DENY_JOIN_REQUEST, callId),
        joinRequestId,
      ),
      {},
    );
  }

  async removeParticipant(
    callId: string,
    participantId: string,
  ): Promise<void> {
    return await this.post<void>(
      withParticipantId(
        withId(ENDPOINTS.VIDEO_CALLS.REMOVE_PARTICIPANT, callId),
        participantId,
      ),
      {},
    );
  }

  async endCall(callId: string, extras: AuthRequestConfig = {}): Promise<void> {
    return await this.post<void>(
      withId(ENDPOINTS.VIDEO_CALLS.END_CALL, callId),
      {},
      extras,
    );
  }

  async previewInvitation(token: string): Promise<VideoCallInvitationPreview> {
    const response = await this.post<{ call: VideoCallInvitationPreview }>(
      ENDPOINTS.VIDEO_CALLS.PREVIEW_INVITATION,
      { token },
      createPublicRequestConfig(),
    );
    return response.call;
  }

  async joinInvitation(
    token: string,
    name: string,
  ): Promise<VideoCallJoinResponse> {
    return await this.post<VideoCallJoinResponse>(
      ENDPOINTS.VIDEO_CALLS.JOIN_INVITATION,
      { token, name },
      createPublicRequestConfig(),
    );
  }

  async getGuestJoinRequest(
    joinRequestId: string,
    guestSecret: string,
  ): Promise<VideoCallJoinRequestResponse> {
    return await this.get<VideoCallJoinRequestResponse>(
      withJoinRequestId(
        ENDPOINTS.VIDEO_CALLS.GUEST_JOIN_REQUEST,
        joinRequestId,
      ),
      {},
      guestRequestConfig(guestSecret),
    );
  }

  async linkGuestAccount(
    joinRequestId: string,
    guestSecret: string,
  ): Promise<VideoCallJoinRequestResponse> {
    return await this.post<VideoCallJoinRequestResponse>(
      withJoinRequestId(
        ENDPOINTS.VIDEO_CALLS.LINK_GUEST_ACCOUNT,
        joinRequestId,
      ),
      { guest_secret: guestSecret },
      {
        suppressAuthFailureRedirect: true,
      },
    );
  }

  async getGuestStudentSignupEligibility(
    joinRequestId: string,
    guestSecret: string,
  ): Promise<VideoCallStudentSignupEligibility> {
    return await this.get<VideoCallStudentSignupEligibility>(
      withJoinRequestId(
        ENDPOINTS.VIDEO_CALLS.GUEST_SIGNUP_ELIGIBILITY,
        joinRequestId,
      ),
      {},
      guestRequestConfig(guestSecret),
    );
  }

  async signupGuestStudentWithPassword(
    joinRequestId: string,
    guestSecret: string,
    account: VideoCallStudentPasswordSignupInput,
  ): Promise<VideoCallStudentSignupResponse> {
    return await this.post<VideoCallStudentSignupResponse>(
      withJoinRequestId(
        ENDPOINTS.VIDEO_CALLS.GUEST_SIGNUP_PASSWORD,
        joinRequestId,
      ),
      {
        ...account,
        email: normalizeEmailInput(account.email),
      },
      guestRequestConfig(guestSecret),
    );
  }

  async signupGuestStudentWithGoogle(
    joinRequestId: string,
    guestSecret: string,
    account: VideoCallStudentGoogleSignupInput,
  ): Promise<VideoCallStudentSignupResponse> {
    return await this.post<VideoCallStudentSignupResponse>(
      withJoinRequestId(
        ENDPOINTS.VIDEO_CALLS.GUEST_SIGNUP_GOOGLE,
        joinRequestId,
      ),
      account,
      guestRequestConfig(guestSecret),
    );
  }

  async updateGuestConnection(
    joinRequestId: string,
    guestSecret: string,
    state: "connected" | "disconnected" | "left",
  ): Promise<VideoCallConnectionResponse> {
    return await this.post<VideoCallConnectionResponse>(
      withJoinRequestId(ENDPOINTS.VIDEO_CALLS.GUEST_CONNECTION, joinRequestId),
      { state },
      guestRequestConfig(guestSecret),
    );
  }
}
